import type { DragEndEvent } from '@dnd-kit/core';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Button, Empty } from 'antd';
import React, { useCallback } from 'react';

import { SCard } from 'app/components/StaycationUI';
import type { DisclaimerKind, PackageDisclaimer } from 'app/typings/packages';

import { DisclaimerBloc } from '../DisclaimerBloc/DisclaimerBloc';

import './DisclaimerCategory.scss';

type Mode = 'view' | 'edit';

type OnChangeArg = {
  id: number | null;
  text: string;
  startDate: string | null;
  endDate: string | null;
  priority: number;
  temporaryId: number;
};

type Props = {
  disclaimers: PackageDisclaimer[];
  kind: DisclaimerKind;
  mode: Mode;
  packageId: number;
  onValueChanged: (
    disclaimers: PackageDisclaimer[],
    kind: DisclaimerKind
  ) => void;
};

type ContainerProps = {
  disclaimers: PackageDisclaimer[];
  mode: Mode;
  onChange: (index: number) => (values: OnChangeArg) => void;
  onRemove: (index: number) => () => void;
  onAdd: () => void;
  onDragEnd: (event: DragEndEvent) => void;
};

const getDisclaimerDndId = (disclaimer: PackageDisclaimer): string =>
  String(disclaimer.id ?? disclaimer.temporaryId);

export const DisclaimerContainer = ({
  disclaimers,
  mode,
  onChange,
  onRemove,
  onAdd,
  onDragEnd,
}: ContainerProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 },
    })
  );

  const items = disclaimers.map(getDisclaimerDndId);

  return (
    <div className="scard-container">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={items} strategy={verticalListSortingStrategy}>
          {disclaimers.map((disclaimer, index) => (
            <DisclaimerBloc
              key={getDisclaimerDndId(disclaimer)}
              dndId={getDisclaimerDndId(disclaimer)}
              id={disclaimer.id}
              priority={disclaimer.priority}
              mode={mode}
              text={disclaimer.text}
              startDate={disclaimer.startDate}
              endDate={disclaimer.endDate}
              temporaryId={disclaimer.temporaryId}
              onChange={onChange(index)}
              onRemove={onRemove(index)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {mode === 'edit' && (
        <div className="container-button">
          <Button size="middle" onClick={onAdd}>
            + Add
          </Button>
        </div>
      )}
      {mode === 'view' && !disclaimers.length && <Empty />}
    </div>
  );
};

export const DisclaimerCategory = ({
  disclaimers,
  kind,
  mode,
  packageId,
  onValueChanged,
}: Props) => {
  function createDisclaimer(values: PackageDisclaimer): PackageDisclaimer {
    const {
      id,
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      kind,
      text,
      startDate,
      endDate,
      // eslint-disable-next-line @typescript-eslint/no-shadow -- inner-scope reuse, not a bug
      packageId,
      priority,
      temporaryId,
    } = values;

    return {
      id: id || null,
      kind,
      text,
      startDate,
      endDate,
      packageId,
      temporaryId: temporaryId || new Date().getTime(),
      priority,
    };
  }

  const onChange = useCallback(
    (index: number) => (values: OnChangeArg) => {
      const newDisclaimer = createDisclaimer({
        ...values,
        packageId,
        kind,
      });
      const newDisclaimersValues = [
        ...disclaimers.slice(0, index),
        newDisclaimer,
        ...disclaimers.slice(index + 1, disclaimers.length),
      ];

      onValueChanged(newDisclaimersValues, kind);
    },
    [disclaimers, kind, onValueChanged, packageId]
  );

  const onDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = disclaimers.findIndex(
        (d) => getDisclaimerDndId(d) === active.id
      );
      const newIndex = disclaimers.findIndex(
        (d) => getDisclaimerDndId(d) === over.id
      );

      if (oldIndex === -1 || newIndex === -1) {
        return;
      }

      const reordered = arrayMove(disclaimers, oldIndex, newIndex).map(
        (d, index) => ({ ...d, priority: index + 1 })
      );

      onValueChanged(reordered, kind);
    },
    [disclaimers, kind, onValueChanged]
  );

  const onAdd = useCallback(() => {
    const newDisclaimer = createDisclaimer({
      text: '',
      kind,
      packageId,
      id: null,
      endDate: null,
      startDate: null,
      priority: disclaimers.length + 1,
      temporaryId: new Date().getTime(),
    });

    onValueChanged([...disclaimers, newDisclaimer], kind);
  }, [onValueChanged, disclaimers, kind, packageId]);

  const onRemove = useCallback(
    (index: number) => () => {
      const newDisclaimersValues = [
        ...disclaimers.slice(0, index),
        ...disclaimers
          .slice(index + 1, disclaimers.length)
          .map((d) => ({ ...d, priority: d.priority - 1 })),
      ];

      onValueChanged(newDisclaimersValues, kind);
    },
    [disclaimers, kind, onValueChanged]
  );

  const title =
    kind === 'ADVICE' ? 'Advice (Cool to know)' : 'Alert (Attention please)';

  return (
    <SCard title={title}>
      <DisclaimerContainer
        disclaimers={disclaimers}
        mode={mode}
        onChange={onChange}
        onRemove={onRemove}
        onAdd={onAdd}
        onDragEnd={onDragEnd}
      />
    </SCard>
  );
};
